import { useState } from 'react';
import { BookOpen, Braces, Compass, GraduationCap, Network, type LucideIcon } from 'lucide-react';
import type { WorldCategory } from '../../types/world';
import { getCatalogText } from '../../utils/catalogText';

const icons = new Map<string, LucideIcon>([['braces', Braces], ['book-open', BookOpen], ['compass', Compass], ['graduation-cap', GraduationCap], ['logic', Network]]);
const symbols = new Map([['lambda', 'λ'], ['varphi', 'φ']]);

export function CategoryIcon({ name, size = 24 }: { name?: string; size?: number }) {
  const symbol = symbols.get(name ?? '');
  if (symbol) return <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center font-serif leading-none" style={{ width: size, height: size, fontSize: size * 1.25 }}>{symbol}</span>;
  const Icon = icons.get(name ?? '') ?? BookOpen;
  return <Icon size={size} aria-hidden="true" />;
}

export function CategoryInstitution({ category, compact = false }: { category: WorldCategory; compact?: boolean }) {
  const [failedLogo, setFailedLogo] = useState<string>();
  if (!category.logo || failedLogo === category.logo) return null;
  const isTukeLogo = category.logo === '/brand/tuke-symbol.svg';
  const image = <img src={category.logo} alt={category.institution ?? `${category.name} logo`} onError={() => setFailedLogo(category.logo)} className={`h-full w-full object-contain ${isTukeLogo ? 'dark:invert' : ''}`} />;
  const classes = `inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-[#ffffff] p-2 dark:border-gray-600 ${isTukeLogo ? 'dark:bg-black' : ''} ${compact ? 'h-12 w-12' : 'h-16 w-16'}`;
  return category.website
    ? <a href={category.website} target="_blank" rel="noopener noreferrer" className={`${classes} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-500`}>{image}<span className="sr-only"> (opens in a new tab)</span></a>
    : <span className={classes}>{image}</span>;
}

export function CategoryMetadata({ category }: { category: WorldCategory }) {
  const text = getCatalogText();
  return <div className="space-y-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
    {category.institution && <p>{category.institution}</p>}
    {category.academicYear && <p>{text.academicYear} <span className="font-medium text-gray-800 dark:text-gray-100">{category.academicYear}</span></p>}
  </div>;
}
